import head from 'lodash/head';

import { mapObjectKeys, mapObjectKeyValues } from 'modules/utils/ObjectUtils';

import {
  MAPPED_KEYS_FACILITIES_RESPONSE,
  MAPPED_LOCATION_TYPE_KEYS,
  serviceName
} from './constants';
import { FacilitiesResponse, Facility } from './types';

type ResponseFormat = {
  emergencyRoomsLocations: Object[];
  instacaresLocations: Object[];
  kidscaresLocations: Object[];
};

function getFacilities(facilities: FacilitiesResponse) {
  const mappedKeys = MAPPED_LOCATION_TYPE_KEYS[serviceName];

  return mapObjectKeys(facilities, mappedKeys);
}

function formatFacilities(facilities: ResponseFormat) {
  const formatValues = {
    locationImageUrl: (url: string) => {
      if (serviceName === 'legacy') return url;

      const baseUrl =
        'https://intermountainhealthcare.org/webservices/locations/hhlocationsearch.asmx/FindLocations';

      return `${baseUrl}${url}`;
    },
    operatingHours: (operatingHours: Object) => {
      if (serviceName === 'legacy') return operatingHours;

      const nameDays = {
        MondayHours: 'Monday',
        TuesdayHours: 'Tuesday',
        WednesdayHours: 'Wednesday',
        ThursdayHours: 'Thursday',
        FridayHours: 'Friday',
        SaturdayHours: 'Saturday',
        SundayHours: 'Sunday',
        Is24Hour: 'is24Hours'
      };

      return mapObjectKeys(operatingHours as { [key in keyof typeof nameDays]: unknown }, nameDays);
    }
  };

  const mappedKeys = MAPPED_KEYS_FACILITIES_RESPONSE[serviceName];
  type LocationType = { [key in keyof typeof mappedKeys]: string };

  const mapLocationFormat = (location: Object) =>
    mapObjectKeyValues(location as LocationType, mappedKeys, formatValues);

  return {
    emergencyRoomsLocations: facilities.emergencyRoomsLocations.map(mapLocationFormat),
    instacaresLocations: facilities.instacaresLocations.map(mapLocationFormat),
    kidscaresLocations: facilities.kidscaresLocations.map(mapLocationFormat)
  };
}

export function getLocationsFormatted(facilities: FacilitiesResponse) {
  const facilitiesResponse = getFacilities(facilities) as ResponseFormat;
  const formattedFacilities = formatFacilities(facilitiesResponse);

  return formattedFacilities;
}

export function getNearestFacility({
  emergencyRoomsLocations,
  instacaresLocations,
  kidscaresLocations
}: {
  emergencyRoomsLocations: Facility[];
  instacaresLocations: Facility[];
  kidscaresLocations: Facility[];
}) {
  return {
    nearestEmergencyRoom: head(emergencyRoomsLocations),
    nearestInstacare: head(instacaresLocations),
    nearestKidscare: head(kidscaresLocations)
  };
}

export function getNearestOpenFacility({
  emergencyRoomsLocations,
  instacaresLocations,
  kidscaresLocations
}: {
  emergencyRoomsLocations: Facility[];
  instacaresLocations: Facility[];
  kidscaresLocations: Facility[];
}) {
  const isOpen = (facility: Facility): Boolean => facility.isCurrentlyOpen;

  return {
    nearestOpenEmergencyRoom: emergencyRoomsLocations.find(isOpen),
    nearestOpenInstacare: instacaresLocations.find(isOpen),
    nearestOpenKidscare: kidscaresLocations.find(isOpen)
  };
}
